import type { LucideIcon } from "lucide-react";

export type WorkspaceTabKind = "route" | "detail";

export type WorkspaceModuleId =
  | "geral"
  | "comercial"
  | "marcas"
  | "patentes"
  | "di"
  | "software"
  | "autoral"
  | "operacional"
  | "internacional"
  | "admin";

export type WorkspaceTabRecord = {
  id: string;
  path: string;
  label: string;
  icon?: LucideIcon;
  pinned: boolean;
  kind: WorkspaceTabKind;
  restorationKey: string;
  updatedAt: string;
};

export const WORKSPACE_STATE_VERSION = 2;
export const WORKSPACE_TABS_KEY = "@nipsys:workspace_tabs";
export const WORKSPACE_ACTIVE_TAB_KEY = "@nipsys:workspace_active_tab";
export const WORKSPACE_TABS_BY_MODULE_KEY = "@nipsys:workspace_tabs_by_module";
export const WORKSPACE_ACTIVE_TAB_BY_MODULE_KEY = "@nipsys:workspace_active_tab_by_module";
export const WORKSPACE_SNAPSHOTS_KEY = "@nipsys:workspace_tab_snapshots";

export const DEFAULT_WORKSPACE_TAB: WorkspaceTabRecord = {
  id: "/geral",
  path: "/geral",
  label: "Página Inicial",
  pinned: false,
  kind: "route",
  restorationKey: "tab:/geral",
  updatedAt: "1970-01-01T00:00:00.000Z",
};

export type WorkspaceTabsByModule = Record<WorkspaceModuleId, WorkspaceTabRecord[]>;
export type WorkspaceActiveTabByModule = Partial<Record<WorkspaceModuleId, string | null>>;

const WORKSPACE_ROUTE_LABELS: Record<string, string> = {
  "/geral": "Página Inicial",
  "/geral/bi-estrategico": "BI Estratégico",
  "/geral/bi-operacional": "BI Operacional",
  "/comercial": "Página Inicial CRM",
  "/comercial/atendimentos": "Atendimentos",
  "/comercial/kanban": "Pipeline",
  "/comercial/clientes": "Clientes",
  "/marcas": "Página Inicial TM",
  "/marcas/busca-global": "Buscas TM",
  "/marcas/busca-inpi": "Buscas TM",
  "/marcas/meus-processos": "Meus Processos TM",
  "/marcas/monitor": "Monitoramento TM",
  "/marcas/email": "E-mail ao Cliente",
  "/marcas/extras": "Extras",
  "/patentes": "Página Inicial Pat",
  "/patentes/busca": "Buscas Pat",
  "/patentes/busca-global": "Buscas Pat",
  "/patentes/meus": "Meus Processos Pat",
  "/patentes/processos": "Meus Processos Pat",
  "/patentes/monitor": "Monitoramento Patentes",
  "/patentes/redacao": "NIP Writer",
  "/patentes/nip-search": "NIP Pat Search",
  "/di": "Página Inicial DI",
  "/di/busca-inpi": "Buscas DI",
  "/di/meus-processos": "Meus Processos DI",
  "/di/monitor": "Monitoramento DI",
  "/di/email": "E-mail ao Cliente",
};

const WORKSPACE_ROUTE_ALIASES: Record<string, string> = {
  "/bi/estrategico": "/geral/bi-estrategico",
  "/bi/operacional": "/geral/bi-operacional",
  "/executivo": "/geral",
  "/gerente": "/geral/bi-estrategico",
  "/marcas/nip-search": "/marcas/busca-global",
  "/di/nip-search": "/di/busca-inpi",
};

const WORKSPACE_MODULE_MATCHERS: Array<{ moduleId: WorkspaceModuleId; matchers: string[] }> = [
  { moduleId: "comercial", matchers: ["/comercial", "/crm", "/clients"] },
  { moduleId: "patentes", matchers: ["/patentes", "/research", "/monitoring"] },
  { moduleId: "marcas", matchers: ["/marcas"] },
  { moduleId: "di", matchers: ["/di"] },
  { moduleId: "operacional", matchers: ["/operacional", "/demands"] },
  { moduleId: "internacional", matchers: ["/internacional"] },
  { moduleId: "software", matchers: ["/software"] },
  { moduleId: "autoral", matchers: ["/autoral"] },
  { moduleId: "admin", matchers: ["/platform", "/settings", "/operations/"] },
  { moduleId: "geral", matchers: ["/geral", "/dashboard", "/alerts", "/processos-unificados"] },
];

export function normalizeWorkspaceRoutePath(path: string) {
  return WORKSPACE_ROUTE_ALIASES[path] ?? path;
}

export function getWorkspaceModuleIdForPath(path: string): WorkspaceModuleId {
  const normalizedPath = normalizeWorkspaceRoutePath(path);
  const matched = WORKSPACE_MODULE_MATCHERS.find(({ matchers }) =>
    matchers.some((matcher) => normalizedPath.startsWith(matcher)),
  );
  return matched?.moduleId ?? "geral";
}

export function getWorkspaceRouteLabel(path: string) {
  return WORKSPACE_ROUTE_LABELS[normalizeWorkspaceRoutePath(path)];
}

export function createEmptyWorkspaceTabsByModule(): WorkspaceTabsByModule {
  return {
    geral: [DEFAULT_WORKSPACE_TAB],
    comercial: [],
    marcas: [],
    patentes: [],
    di: [],
    software: [],
    autoral: [],
    operacional: [],
    internacional: [],
    admin: [],
  };
}

function getCurrentIsoTimestamp() {
  return new Date().toISOString();
}

function isUuidLike(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function isLikelyDetailIdentifier(value: string) {
  if (!value) return false;
  if (/^\d+$/.test(value)) return true;
  if (isUuidLike(value)) return true;
  return /^(?=.*\d)[a-z0-9_-]{3,}$/i.test(value);
}

export function inferWorkspaceTabMetadata(path: string): Pick<WorkspaceTabRecord, "kind" | "restorationKey"> {
  const normalizedPath = normalizeWorkspaceRoutePath(path);
  const pathSegments = normalizedPath.split("/").filter(Boolean);
  const parentSegment = pathSegments.at(-2);
  const leafSegment = pathSegments.at(-1);

  if (parentSegment && leafSegment && isLikelyDetailIdentifier(leafSegment)) {
    return {
      kind: "detail",
      restorationKey: `${parentSegment}:${leafSegment}`,
    };
  }

  return {
    kind: "route",
    restorationKey: `tab:${normalizedPath}`,
  };
}

export function buildWorkspaceFallbackLabel(path: string) {
  const normalizedPath = normalizeWorkspaceRoutePath(path);
  const mappedLabel = getWorkspaceRouteLabel(normalizedPath);
  if (mappedLabel) return mappedLabel;

  const pathSegments = normalizedPath.split("/").filter(Boolean);
  if (pathSegments.length === 0) return DEFAULT_WORKSPACE_TAB.label;
  const lastSegment = pathSegments[pathSegments.length - 1] || "NIPSYS";
  return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
}

function normalizePersistedTabLabel(
  path: string,
  kind: WorkspaceTabKind,
  storedLabel: string | null,
) {
  const canonicalRouteLabel = kind === "route" ? getWorkspaceRouteLabel(path) : undefined;
  if (canonicalRouteLabel) {
    return canonicalRouteLabel;
  }

  if (storedLabel?.trim()) {
    return storedLabel.trim();
  }

  return buildWorkspaceFallbackLabel(path);
}

export function migrateStoredTabs(value: unknown): WorkspaceTabRecord[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [DEFAULT_WORKSPACE_TAB];
  }

  const seen = new Set<string>();
  const tabs = value
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === "object")
    .map((entry) => {
      const path = typeof entry.path === "string" ? entry.path.trim() : "";
      const normalizedPath = normalizeWorkspaceRoutePath(path);
      const id = typeof entry.id === "string" && entry.id.trim() ? normalizeWorkspaceRoutePath(entry.id.trim()) : normalizedPath;

      if (!normalizedPath || !id) {
        return null;
      }

      const kind = entry.kind === "detail" ? "detail" : "route";
      const label = normalizePersistedTabLabel(normalizedPath, kind, typeof entry.label === "string" ? entry.label : null);

      return {
        id,
        path: normalizedPath,
        label,
        pinned: typeof entry.pinned === "boolean" ? entry.pinned : false,
        kind,
        restorationKey:
          typeof entry.restorationKey === "string" && entry.restorationKey.trim()
            ? entry.restorationKey.trim()
            : `tab:${id}`,
        updatedAt:
          typeof entry.updatedAt === "string" && entry.updatedAt.trim()
            ? entry.updatedAt.trim()
            : getCurrentIsoTimestamp(),
      } satisfies WorkspaceTabRecord;
    })
    .filter((tab): tab is WorkspaceTabRecord => Boolean(tab))
    .filter((tab) => {
      if (seen.has(tab.id)) {
        return false;
      }

      seen.add(tab.id);
      return true;
    });

  return tabs.length > 0 ? tabs : [DEFAULT_WORKSPACE_TAB];
}

export function closeAllNonPinnedTabs(tabs: WorkspaceTabRecord[], activeTabId?: string | null) {
  const pinnedTabs = tabs.filter((tab) => tab.pinned);
  const survivingActiveTab = activeTabId ? pinnedTabs.find((tab) => tab.id === activeTabId) ?? null : null;
  const orderedPinnedTabs = survivingActiveTab
    ? [survivingActiveTab, ...pinnedTabs.filter((tab) => tab.id !== survivingActiveTab.id)]
    : pinnedTabs;
  const nextTabs = orderedPinnedTabs.length > 0 ? orderedPinnedTabs : [DEFAULT_WORKSPACE_TAB];
  const nextActiveTabId = nextTabs[0]?.id ?? DEFAULT_WORKSPACE_TAB.id;

  return {
    tabs: nextTabs,
    activeTabId: nextActiveTabId,
  };
}
